// src/pages/ModeratorCriteriaCategoriesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "../api/axios";
import "./ModeratorCriteriaCategoriesPage.css";

/* ===========================================================
   Строка "санат + процент" внутри формы категории критериев
=========================================================== */
function CategoryPercentRow({
  row,
  index,
  options,
  usedIds,
  onChange,
  onRemove,
  canRemove,
  locale,
  L,
}) {
  // санаты, которые уже выбраны в ДРУГИХ строках, скрываем из этого селекта,
  // но текущий выбор этой строки оставляем доступным
  const availableOptions = options.filter(
    (o) => !usedIds.has(Number(o.value)) || Number(o.value) === Number(row.category_id)
  );

  return (
    <div className="ccp-row">
      <select
        className="ccp-row-select"
        value={row.category_id}
        onChange={(e) => onChange(index, "category_id", e.target.value)}
      >
        <option value="">{L.pickCategory}</option>
        {availableOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      <input
        type="number"
        min="0"
        max="100"
        step="0.5"
        className="ccp-row-percent"
        value={row.percent}
        onChange={(e) => onChange(index, "percent", e.target.value)}
        placeholder="%"
      />

      <button
        type="button"
        className="ccp-row-remove"
        onClick={() => onRemove(index)}
        disabled={!canRemove}
        title={L.removeRow}
      >
        ✕
      </button>
    </div>
  );
}

export default function ModeratorCriteriaCategoriesPage({ locale = "kz" }) {
  const [criteriaCategories, setCriteriaCategories] = useState([]);
  const [refCategories, setRefCategories] = useState([]); // санаты (categories)

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (santatId) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      const id = Number(santatId);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const emptyForm = { name_kz: "", name_ru: "", rows: [{ category_id: "", percent: "" }] };
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const t = useMemo(
    () => ({
      kz: {
        title: "Критерий санаттары",
        add: "Санат қосу",
        edit: "Өңдеу",
        save: "Сақтау",
        del: "Жою",
        empty: "Санаттар жоқ",
        modalAdd: "Санат қосу",
        modalEdit: "Санатты өңдеу",
        nameKz: "Атауы (қазақша)",
        nameRu: "Атауы (орысша)",
        categoriesLabel: "Санаттар мен пайыздар",
        categoriesHint:
            "Әр жол — санат (тестке қатысатын жалпы бөлім) және сол санат ішіндегі осы категорияның салмағы (%). Бір санатты екі рет таңдауға болмайды. Кемінде бір жол міндетті.",
        addRow: "Жол қосу",
        removeRow: "Жолды жою",
        pickCategory: "Санатты таңдаңыз",
        confirmDel: "Осы санатты жоюға сенімдісіз бе?",
        err: "Қате орын алды",
        forbidden: "Сізде бұл бетке қол жеткізу құқығы жоқ (модератор рөлі қажет).",
        requiredField: "Міндетті өріс",
        percentRange: "0-ден 100-ге дейінгі сан болуы керек",
        rowsRequired: "Кемінде бір санат жолын толтырыңыз",
        rowsDuplicate: "Бір санатты бірнеше рет таңдауға болмайды",
        loading: "Жүктелуде...",
        cancel: "Болдырмау",
        sumOk: "Жақсы, қосынды 100% құрайды.",
        sumLow: (d) => `100%-ға жету үшін тағы ${d}% жетіспейді.`,
        sumHigh: (d) => `100%-дан ${d}% артық.`,
      },
      ru: {
        title: "Категории критериев",
        add: "Добавить категорию",
        edit: "Редактировать",
        save: "Сохранить",
        del: "Удалить",
        empty: "Категорий нет",
        modalAdd: "Добавить категорию",
        modalEdit: "Редактирование категории",
        nameKz: "Название (каз)",
        nameRu: "Название (рус)",
        categoriesLabel: "Санаты и проценты",
        categoriesHint:
          "Каждая строка — санат (общий раздел) и вес этой категории критериев именно внутри него (%). Один санат нельзя выбрать дважды. Минимум одна строка обязательна.",
        addRow: "Добавить строку",
        removeRow: "Удалить строку",
        pickCategory: "Выберите санат",
        confirmDel: "Вы уверены, что хотите удалить эту категорию?",
        err: "Произошла ошибка",
        forbidden: "У вас нет доступа к этой странице (требуется роль модератора).",
        requiredField: "Обязательное поле",
        percentRange: "Должно быть числом от 0 до 100",
        rowsRequired: "Заполните хотя бы одну строку санат/процент",
        rowsDuplicate: "Один санат нельзя выбрать в нескольких строках",
        loading: "Загрузка...",
        cancel: "Отмена",
        sumOk: "Отлично, сумма равна 100%.",
        sumLow: (d) => `До 100% не хватает ${d}%.`,
        sumHigh: (d) => `Превышение над 100% на ${d}%.`,
      },
    }),
    []
  );

  const L = locale === "kz" ? t.kz : t.ru;

  const fetchAll = async () => {
    setLoading(true);
    setAccessDenied(false);
    setLoadError("");
    try {
      const [catRes, refCatRes] = await Promise.all([
        axios.get("/backoffice/criteria-categories"),
        axios.get("/backoffice/ref/categories"),
      ]);
      setCriteriaCategories(Array.isArray(catRes.data) ? catRes.data : []);
      setRefCategories(Array.isArray(refCatRes.data) ? refCatRes.data : []);
    } catch (err) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setLoadError(err?.response?.data?.message || L.err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refCategoryOptions = useMemo(
    () =>
      refCategories.map((c) => ({
        value: c.id,
        label: locale === "kz" ? c.name_kz || c.name : c.name_ru || c.name,
      })),
    [refCategories, locale]
  );

  const refCatById = useMemo(() => {
    const m = new Map();
    refCategories.forEach((c) => m.set(Number(c.id), c));
    return m;
  }, [refCategories]);

  // группировка списка категорий критериев по санату для отображения
  const groups = useMemo(() => {
    const map = new Map(); // santat_id -> { santat, items: [{ criteriaCategory, percent }] }

    refCategories.forEach((santat) => {
      map.set(Number(santat.id), { santat, items: [] });
    });

    criteriaCategories.forEach((cc) => {
      (cc.categories || []).forEach((catEntry) => {
        const santatId = Number(catEntry.id);
        if (!map.has(santatId)) {
          map.set(santatId, { santat: catEntry, items: [] });
        }
        map.get(santatId).items.push({
          criteriaCategory: cc,
          percent: Number(catEntry.percent) || 0,
        });
      });
    });

    return Array.from(map.values()).filter((g) => g.items.length > 0 || refCatById.has(Number(g.santat.id)));
  }, [criteriaCategories, refCategories, refCatById]);

  const openAddModal = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setEditingId(null);
    setIsEditing(false);
    setModalOpen(true);
  };

  const openEditModal = (cat) => {
    const rows =
      Array.isArray(cat?.categories) && cat.categories.length > 0
        ? cat.categories.map((c) => ({ category_id: String(c.id), percent: String(c.percent) }))
        : [{ category_id: "", percent: "" }];
    setFormData({
      name_kz: cat?.name_kz || "",
      name_ru: cat?.name_ru || "",
      rows,
    });
    setFormErrors({});
    setEditingId(cat?.id);
    setIsEditing(true);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const addRow = () => {
    setFormData((f) => ({ ...f, rows: [...f.rows, { category_id: "", percent: "" }] }));
  };

  const removeRow = (index) => {
    setFormData((f) => ({ ...f, rows: f.rows.filter((_, i) => i !== index) }));
  };

  const updateRow = (index, field, value) => {
    setFormData((f) => ({
      ...f,
      rows: f.rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    }));
  };

  const usedCategoryIds = useMemo(
    () => new Set(formData.rows.map((r) => Number(r.category_id)).filter((v) => !Number.isNaN(v) && v !== 0)),
    [formData.rows]
  );

  const validate = () => {
    const errs = {};
    if (!formData.name_kz?.trim()) errs.name_kz = L.requiredField;
    if (!formData.name_ru?.trim()) errs.name_ru = L.requiredField;

    if (!formData.rows || formData.rows.length === 0) {
      errs._rows = L.rowsRequired;
    } else {
      const seen = new Set();
      let rowError = "";
      for (const r of formData.rows) {
        if (String(r.category_id).trim() === "" || String(r.percent).trim() === "") {
          rowError = L.rowsRequired;
          break;
        }
        const p = Number(r.percent);
        if (Number.isNaN(p) || p < 0 || p > 100) {
          rowError = L.percentRange;
          break;
        }
        const cid = Number(r.category_id);
        if (seen.has(cid)) {
          rowError = L.rowsDuplicate;
          break;
        }
        seen.add(cid);
      }
      if (rowError) errs._rows = rowError;
    }

    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const payload = {
      name_kz: formData.name_kz.trim(),
      name_ru: formData.name_ru.trim(),
      categories: formData.rows.map((r) => ({
        category_id: Number(r.category_id),
        percent: Math.round(Number(r.percent) * 100) / 100,
      })),
    };

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`/backoffice/criteria-categories/${editingId}`, payload);
      } else {
        await axios.post("/backoffice/criteria-categories", payload);
      }
      await fetchAll();
      setModalOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setAccessDenied(true);
        setModalOpen(false);
      } else if (status === 422) {
        const data = err?.response?.data;
        const serverErrors = data?.errors || {};
        const mapped = {};
        Object.keys(serverErrors).forEach((key) => {
          const msgs = serverErrors[key];
          mapped[key] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
        });
        setFormErrors((prev) => ({ ...prev, ...mapped }));
        if (Object.keys(mapped).length === 0 && data?.message) {
          setFormErrors((prev) => ({ ...prev, _general: data.message }));
        }
      } else {
        setFormErrors((prev) => ({
          ...prev,
          _general: err?.response?.data?.message || L.err,
        }));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditing || !editingId) return;
    if (!window.confirm(L.confirmDel)) return;

    setSaving(true);
    try {
      await axios.delete(`/backoffice/criteria-categories/${editingId}`);
      await fetchAll();
      setModalOpen(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
        setModalOpen(false);
      } else {
        alert(err?.response?.data?.message || L.err);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ccp-page">
        <div className="ccp-status">{L.loading}</div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="ccp-page">
        <div className="ccp-forbidden">🚫 {L.forbidden}</div>
      </div>
    );
  }

  return (
    <div className="ccp-page">
      <div className="ccp-head">
        <h2>🏷️ {L.title}</h2>
        <button className="ccp-btn ccp-btn-primary" type="button" onClick={openAddModal}>
          ➕ {L.add}
        </button>
      </div>

      {loadError && <div className="ccp-error-banner">{loadError}</div>}

      <div className="ccp-groups">
        {groups.map((g) => {
          const sum = Math.round(g.items.reduce((acc, it) => acc + it.percent, 0) * 100) / 100;
          const diff = Math.round((100 - sum) * 100) / 100;
          const isOk = Math.abs(diff) < 0.001;
          const isCollapsed = !expandedGroups.has(Number(g.santat.id));

          return (
            <div key={g.santat.id} className={`ccp-group ${isOk ? "ccp-group--ok" : "ccp-group--warn"}`}>
              <div
                className={`ccp-group-head ${isCollapsed ? "ccp-group-head--collapsed" : ""}`}
                onClick={() => toggleGroup(g.santat.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleGroup(g.santat.id);
                  }
                }}
              >
                <div className="ccp-group-head-left">
                  <span className={`ccp-group-toggle ${isCollapsed ? "ccp-group-toggle--collapsed" : ""}`}>
                    ▾
                  </span>
                  <h3 className="ccp-group-title">
                    {locale === "kz" ? g.santat.name_kz || g.santat.name : g.santat.name_ru || g.santat.name}
                  </h3>
                </div>
                <span className={`ccp-group-sum ${isOk ? "ccp-group-sum--ok" : "ccp-group-sum--warn"}`}>
                  {isOk ? "✅" : "⚠️"} {sum}%
                  {!isOk && (
                    <span className="ccp-group-sum-hint">
                      {" "}
                      — {diff > 0 ? L.sumLow(diff) : L.sumHigh(Math.abs(diff))}
                    </span>
                  )}
                </span>
              </div>

              {!isCollapsed && (
              <table className="ccp-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{L.nameKz.split(" ")[0]}</th>
                    <th>%</th>
                    <th>{L.edit}</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map(({ criteriaCategory, percent }) => (
                    <tr key={criteriaCategory.id}>
                      <td>{criteriaCategory.id}</td>
                      <td>{locale === "kz" ? criteriaCategory.name_kz : criteriaCategory.name_ru}</td>
                      <td className="ccp-percent-cell">{percent}%</td>
                      <td>
                        <button
                          className="ccp-btn ccp-btn-ghost ccp-btn-sm"
                          type="button"
                          onClick={() => openEditModal(criteriaCategory)}
                        >
                          ✏️ {L.edit}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {g.items.length === 0 && (
                    <tr>
                      <td colSpan={4} className="ccp-empty-row">
                        {L.empty}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              )}
            </div>
          );
        })}
        {groups.length === 0 && <div className="ccp-empty-row">{L.empty}</div>}
      </div>

      {modalOpen && (
        <div
          className="ccp-backdrop"
          onClick={(e) => {
            if (e.target.classList.contains("ccp-backdrop")) closeModal();
          }}
        >
          <div className="ccp-card" role="dialog" aria-modal="true">
            <div className="ccp-modal-head">
              <h3 className="ccp-title">{isEditing ? L.modalEdit : L.modalAdd}</h3>
            </div>

            <div className="ccp-modal-body">
              {formErrors._general && (
                <div className="ccp-error-banner">{formErrors._general}</div>
              )}

              <div className="ccp-form-grid">
                <label className="ccp-field">
                  <span className="ccp-label">{L.nameKz}</span>
                  <input
                    type="text"
                    value={formData.name_kz}
                    onChange={(e) => setFormData({ ...formData, name_kz: e.target.value })}
                    className={formErrors.name_kz ? "ccp-input-error" : ""}
                  />
                  {formErrors.name_kz && (
                    <small className="ccp-error-text">{formErrors.name_kz}</small>
                  )}
                </label>

                <label className="ccp-field">
                  <span className="ccp-label">{L.nameRu}</span>
                  <input
                    type="text"
                    value={formData.name_ru}
                    onChange={(e) => setFormData({ ...formData, name_ru: e.target.value })}
                    className={formErrors.name_ru ? "ccp-input-error" : ""}
                  />
                  {formErrors.name_ru && (
                    <small className="ccp-error-text">{formErrors.name_ru}</small>
                  )}
                </label>

                <div className="ccp-field ccp-field--rows">
                  <span className="ccp-label">{L.categoriesLabel}</span>
                  <small className="ccp-hint">{L.categoriesHint}</small>

                  <div className="ccp-rows">
                    {formData.rows.map((row, idx) => (
                      <CategoryPercentRow
                        key={idx}
                        row={row}
                        index={idx}
                        options={refCategoryOptions}
                        usedIds={usedCategoryIds}
                        onChange={updateRow}
                        onRemove={removeRow}
                        canRemove={formData.rows.length > 1}
                        locale={locale}
                        L={L}
                      />
                    ))}
                  </div>

                  <button type="button" className="ccp-btn ccp-btn-secondary ccp-btn-sm" onClick={addRow}>
                    ➕ {L.addRow}
                  </button>

                  {formErrors._rows && <small className="ccp-error-text">{formErrors._rows}</small>}
                </div>
              </div>
            </div>

            <div className="ccp-actions">
              {isEditing && (
                <button
                  type="button"
                  className="ccp-btn ccp-btn-danger"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  🗑 {L.del}
                </button>
              )}
              <button
                type="button"
                className="ccp-btn ccp-btn-secondary"
                onClick={closeModal}
                disabled={saving}
              >
                {L.cancel}
              </button>
              <button
                type="button"
                className="ccp-btn ccp-btn-primary"
                onClick={handleSubmit}
                disabled={saving}
              >
                💾 {L.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}